import { useEffect, useState } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import { EditorHeader } from './EditorHeader';
import { NodePaletteShell } from './NodePaletteShell';
import { PropertiesPanelShell } from './PropertiesPanelShell';
import { EditorCanvas } from './EditorCanvas';
import { useEditorStore } from '../state/editor-store';
import { useUnsavedChangesGuard } from '../hooks/use-editor-shortcuts';
import { useAutosave } from '../persistence/use-autosave';
import { useAuthStore } from '@/features/auth/state/auth-store';
import { FUNNEL_SCHEMA_VERSION } from '@/domain/funnel/schema-version';
import { shouldBlockUnload } from '../persistence/navigation-guard';
import { ConflictDialog } from './ConflictDialog';
import { FunnelMetricsSummary } from '../metrics/FunnelMetricsSummary';
import { SimulationPanel } from '../simulation/SimulationPanel';
import { SaveAsTemplateDialog } from '../templates/SaveAsTemplateDialog';
import { PresentationSettingsPanel } from '../presentation/PresentationSettingsPanel';
import { navigate } from '@/app/router';
import { ShareSettingsPanel } from '../sharing/ShareSettingsPanel';
import { ExportDialog } from '@/features/export/ExportDialog';
import { HistoryPanel } from '@/features/history/HistoryPanel';
import { DiagnosisPanel } from '@/features/diagnosis/DiagnosisPanel';
import { AIAssistantPanel } from '@/features/ai/AIAssistantPanel';

function EditorWorkspace({
  id,
  name,
  description,
  revision,
}: {
  id: string;
  name: string;
  description?: string | null;
  revision: number;
}) {
  const graph = useEditorStore((state) => state.graph);
  const layoutError = useEditorStore((state) => state.layoutError);
  const connectionError = useEditorStore((state) => state.connectionError);
  const userId = useAuthStore((state) => state.user?.id) ?? '';
  const autosave = useAutosave({
    funnelId: id,
    userId,
    initialRevision: revision,
    schemaVersion: FUNNEL_SCHEMA_VERSION,
  });
  useUnsavedChangesGuard(shouldBlockUnload(autosave.state));
  const [conflictDialogDismissed, setConflictDialogDismissed] = useState(false);
  const showConflictDialog = autosave.state === 'conflict' && !conflictDialogDismissed;
  const [showSimulation, setShowSimulation] = useState(false);
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
  const [showPresentationSettings, setShowPresentationSettings] = useState(false);
  const [showShareSettings, setShowShareSettings] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showDiagnosis, setShowDiagnosis] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [aiFindingId, setAiFindingId] = useState<string | undefined>();
  const [presentationError, setPresentationError] = useState<string | null>(null);
  const [navigationRetry, setNavigationRetry] = useState<'presentation' | 'documentation'>(
    'presentation',
  );
  const setSelection = useEditorStore((state) => state.setSelection);
  useEffect(() => {
    if (autosave.state !== 'conflict') setConflictDialogDismissed(false);
  }, [autosave.state]);

  useEffect(() => {
    const focusNode = new URLSearchParams(window.location.search).get('focusNode');
    if (focusNode && graph?.nodes.some((node) => node.id === focusNode)) setSelection([focusNode]);
  }, [graph, setSelection]);

  async function enterPresentation() {
    setNavigationRetry('presentation');
    setPresentationError(null);
    const flushed = await autosave.flush();
    if (!flushed) {
      setPresentationError(
        'The latest changes could not be saved. Retry before opening the presentation.',
      );
      return;
    }
    navigate(`/app/funnels/${id}/present`);
  }

  async function enterDocumentation() {
    setNavigationRetry('documentation');
    setPresentationError(null);
    const flushed = await autosave.flush();
    if (!flushed) {
      setPresentationError(
        'The latest changes could not be saved. Retry before opening documentation.',
      );
      return;
    }
    navigate(`/app/funnels/${id}/documentation`);
  }

  return (
    <ReactFlowProvider>
      <div className="flex h-[calc(100vh-3.5rem)] min-h-[520px] flex-col overflow-hidden bg-bg-app">
        <EditorHeader name={name} saveState={autosave.state} onRetry={autosave.retry} />
        {layoutError ? (
          <div
            role="alert"
            className="border-b border-[rgba(226,85,85,0.35)] bg-[rgba(226,85,85,0.1)] px-4 py-2 text-xs text-[#f08484]"
          >
            {layoutError}
          </div>
        ) : null}
        {connectionError ? (
          <div
            role="alert"
            className="border-b border-[rgba(226,85,85,0.35)] bg-[rgba(226,85,85,0.1)] px-4 py-2 text-xs text-[#f08484]"
          >
            {connectionError}
          </div>
        ) : null}
        {presentationError ? (
          <div
            role="alert"
            className="flex items-center justify-between border-b border-[rgba(226,85,85,0.35)] bg-[rgba(226,85,85,0.1)] px-4 py-2 text-xs text-[#f08484]"
          >
            <span>{presentationError}</span>
            <button
              type="button"
              className="font-medium underline"
              onClick={() =>
                void (navigationRetry === 'documentation'
                  ? enterDocumentation()
                  : enterPresentation())
              }
            >
              Retry
            </button>
          </div>
        ) : null}
        {showConflictDialog ? (
          <ConflictDialog
            onReload={() => void autosave.reloadServerVersion()}
            onKeepLocal={() => {
              autosave.dismissConflictKeepLocal();
              setConflictDialogDismissed(true);
            }}
          />
        ) : null}
        <div className="flex items-center justify-between border-b border-border-subtle bg-surface">
          <FunnelMetricsSummary />
          <div className="mr-4 flex shrink-0 gap-2">
            <button
              type="button"
              onClick={() => {
                setShowAI((value) => !value);
                setAiFindingId(undefined);
                setShowHistory(false);
                setShowDiagnosis(false);
                setShowShareSettings(false);
                setShowPresentationSettings(false);
                setShowSimulation(false);
              }}
              className="rounded border border-border-brand bg-[rgba(198,144,50,0.08)] px-2 py-1 text-xs text-text-brand hover:bg-[rgba(198,144,50,0.14)]"
            >
              {showAI ? 'Ocultar IA' : 'Perguntar à IA'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowHistory((value) => !value);
                setShowAI(false);
                setShowDiagnosis(false);
                setShowShareSettings(false);
                setShowPresentationSettings(false);
                setShowSimulation(false);
              }}
              className="rounded border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
            >
              {showHistory ? 'Ocultar histórico' : 'Histórico'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowDiagnosis((value) => !value);
                setShowAI(false);
                setShowHistory(false);
                setShowShareSettings(false);
                setShowPresentationSettings(false);
                setShowSimulation(false);
              }}
              className="rounded border border-border-brand bg-[rgba(198,144,50,0.08)] px-2 py-1 text-xs text-text-brand hover:bg-[rgba(198,144,50,0.14)]"
            >
              {showDiagnosis ? 'Ocultar diagnóstico' : 'Diagnóstico'}
            </button>
            <button
              type="button"
              onClick={() => setShowExport(true)}
              className="rounded border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
            >
              Exportar
            </button>
            <button
              type="button"
              onClick={() => void enterDocumentation()}
              className="rounded border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
            >
              Documentação
            </button>
            <button
              type="button"
              onClick={() => setShowSaveAsTemplate(true)}
              className="rounded border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
            >
              Salvar como modelo
            </button>
            <button
              type="button"
              onClick={() => {
                setShowShareSettings((value) => !value);
                setShowAI(false);
                setShowDiagnosis(false);
                setShowPresentationSettings(false);
                setShowSimulation(false);
                setShowHistory(false);
              }}
              className="rounded border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
            >
              Compartilhar
            </button>
            <button
              type="button"
              onClick={() => {
                setShowPresentationSettings((value) => !value);
                setShowAI(false);
                setShowDiagnosis(false);
                setShowShareSettings(false);
                setShowSimulation(false);
                setShowHistory(false);
              }}
              className="rounded border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
            >
              {showPresentationSettings ? 'Ocultar configurações da apresentação' : 'Configurações da apresentação'}
            </button>
            <button
              type="button"
              onClick={() => void enterPresentation()}
              className="rounded border border-border-brand bg-[rgba(198,144,50,0.08)] px-2 py-1 text-xs text-text-brand hover:bg-[rgba(198,144,50,0.14)]"
            >
              Apresentar
            </button>
            <button
              type="button"
              onClick={() => {
                setShowSimulation((value) => !value);
                setShowAI(false);
                setShowDiagnosis(false);
                setShowShareSettings(false);
                setShowPresentationSettings(false);
                setShowHistory(false);
              }}
              className="rounded border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
            >
              {showSimulation ? 'Ocultar simulação' : 'Simulação'}
            </button>
          </div>
        </div>
        <SaveAsTemplateDialog
          open={showSaveAsTemplate}
          funnelId={id}
          funnelName={name}
          flush={autosave.flush}
          onClose={() => setShowSaveAsTemplate(false)}
        />
        {graph ? (
          <ExportDialog
            open={showExport}
            onClose={() => setShowExport(false)}
            funnelId={id}
            funnelName={name}
            description={description}
            graph={graph}
            flush={autosave.flush}
          />
        ) : null}
        <div className="relative flex min-h-0 flex-1">
          <NodePaletteShell />
          <EditorCanvas />
          {showAI ? (
            <AIAssistantPanel
              funnelId={id}
              funnelName={name}
              description={description}
              initialFindingId={aiFindingId}
              onClose={() => setShowAI(false)}
            />
          ) : showDiagnosis ? (
            <DiagnosisPanel
              onClose={() => setShowDiagnosis(false)}
              onExplainFinding={(findingId) => {
                setAiFindingId(findingId);
                setShowDiagnosis(false);
                setShowAI(true);
              }}
            />
          ) : showHistory ? (
            <HistoryPanel
              funnelId={id}
              currentRevision={autosave.revision}
              flush={autosave.flush}
              onRestored={autosave.reloadServerVersion}
              onClose={() => setShowHistory(false)}
            />
          ) : showShareSettings ? (
            <ShareSettingsPanel
              funnelId={id}
              flush={autosave.flush}
              onClose={() => setShowShareSettings(false)}
            />
          ) : showPresentationSettings ? (
            <PresentationSettingsPanel
              funnelId={id}
              onClose={() => setShowPresentationSettings(false)}
            />
          ) : showSimulation ? (
            <SimulationPanel onClose={() => setShowSimulation(false)} />
          ) : (
            <PropertiesPanelShell funnelId={id} />
          )}
          <div className="pointer-events-none absolute inset-0 z-50 hidden items-center justify-center bg-bg-app/95 px-6 text-center max-[840px]:flex">
            <div>
              <h2 className="text-lg font-semibold">Recomendamos uma tela maior</h2>
              <p className="mt-2 text-sm text-text-secondary">
                O editor visual foi pensado primeiro para desktop. Amplie a janela para continuar editando com segurança.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  );
}

export function EditorShell({
  id,
  name,
  description,
  revision,
}: {
  id: string;
  name: string;
  description?: string | null;
  revision: number;
}) {
  return <EditorWorkspace id={id} name={name} description={description} revision={revision} />;
}
